"use client";
import React, { useState, useEffect, useMemo, useCallback, useRef, useTransition } from "react";
import CustomCalendar from "@/components/ui/CustomCalendar";
import CustomDropdown from "@/components/ui/CustomDropdown";
import Icon from "@/components/icons/Icons";
import { SchemeCatgoryAndId } from "@/type/overlapType";
import { getFundsBySchemeCategory } from "@/lib/prismaController";
import { RollingQuery, FundOption } from "@/type/rollingPageTypes";
import { AllBenchmarkListItem } from "@/type/commonTypes";
import { formatDate, lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import RollingCalcDropdown from "@/app/tools-and-calculators/components/ui/RollingCalcDropdown";
import { fuzzySearch } from "@/utils/commonSearchText";
import { getValidatedRollingPeriod, ROLLING_PERIODS } from "@/utils/rollingReturnsCalculation";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import CustomPageLoader from "@/components/others/CustomPageLoader";

interface TestCalcProps {
  query: RollingQuery;
  categories: SchemeCatgoryAndId[];
  benchmarks: AllBenchmarkListItem[];
  defaultBenchmark?: AllBenchmarkListItem | null;
}

const MAX_FIELDS = 4;

const DEFAULT_START_DATE = "2020-01-01";
const today = new Date();
const minDateLimit = new Date("1990-01-01");

export default function RollingCalcSection({ query, categories, benchmarks, defaultBenchmark }: TestCalcProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Selected Category
  const defaultCategory = useMemo(() => {
    return categories.find((cat) => cat.fund_cat_id === Number(query?.catId ?? 21)) ?? categories[0];
  }, [categories, query?.catId]);

  // initially 2 dropdowns with null value
  const categoryButtonRef = useRef<HTMLButtonElement>(null);
  const benchmarkButtonRef = useRef<HTMLButtonElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [isLoading, startTransition] = useTransition();

  // Category Dropdown
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [searchCategory, setSearchCategory] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<SchemeCatgoryAndId>(defaultCategory);

  // Start Date Dropdown
  const [startDateDropdownOpen, setStartDateDropdownOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<string>(query.startDate ?? DEFAULT_START_DATE);

  // Benchmark Dropdown
  const [benchmarkDropdownOpen, setBenchmarkDropdownOpen] = useState(false);
  const [searchBenchmark, setSearchBenchmark] = useState("");
  const [selectedBenchmark, setSelectedBenchmark] = useState<AllBenchmarkListItem | null>(
    defaultBenchmark ?? benchmarks[0],
  );

  // Category Funds Data
  const [fundOptions, setFundOptions] = useState<FundOption[]>([]);
  const [selectedFunds, setSelectedFunds] = useState<(FundOption | null)[]>([null]);

  // Rolling Period
  const availableYears = useMemo(() => {
    const start = new Date(selectedStartDate).getTime();
    const diff = today.getTime() - start;

    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  }, [selectedStartDate]);

  const validatedPeriod = useMemo(() => {
    return getValidatedRollingPeriod(String(query.period ?? "1"), availableYears);
  }, [query.period, availableYears]);

  const [selectedPeriod, setSelectedPeriod] = useState<string>(validatedPeriod);

  // Default funds based on query params
  useEffect(() => {
    if (!fundOptions.length) return;

    const queryFundIds = query?.fundIds ?? [];
    const fundMap = new Map(fundOptions.map((f) => [String(f.id), f]));

    let mappedFunds: (FundOption | null)[] = [];

    if (queryFundIds.length > 0) {
      mappedFunds = queryFundIds.map((id) => {
        return fundMap.get(String(id)) ?? null;
      });
    }

    // remove completely invalid case (all null)
    const hasAtLeastOneValid = mappedFunds.some((f) => f !== null);

    if (!mappedFunds.length || !hasAtLeastOneValid) {
      mappedFunds = [fundOptions[0] ?? null];
    }

    setSelectedFunds(mappedFunds);
  }, [fundOptions, query?.fundIds]);

  const handleAddField = () => {
    setSelectedFunds((prev) => {
      if (prev.length >= MAX_FIELDS) return prev;
      return [...prev, null];
    });
  };

  const handleRemoveField = (index: number) => {
    setSelectedFunds((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const filteredCategories = useMemo(() => {
    return categories.filter((c) => fuzzySearch(searchCategory, c.fund_category ?? ""));
  }, [categories, searchCategory]);

  const filteredBenchmark = useMemo(() => {
    return benchmarks.filter((item) => fuzzySearch(searchBenchmark, item.stkexchg ?? ""));
  }, [benchmarks, searchBenchmark]);

  useEffect(() => {
    const fetchFunds = async () => {
      const data = await getFundsBySchemeCategory([String(selectedCategory?.fund_cat_id) ?? "21"]);

      setFundOptions(data ?? []);
    };

    fetchFunds();
  }, [selectedCategory]);

  const handleSelectFund = useCallback((index: number, fund: FundOption) => {
    setSelectedFunds((prev) => {
      const updated = [...prev];
      updated[index] = fund;
      return updated;
    });
  }, []);

  const selectedFundIds = useMemo(() => {
    return selectedFunds.map((f) => f?.id).filter((id): id is number => Boolean(id));
  }, [selectedFunds]);

  const getFilteredFunds = useCallback(
    (index: number): FundOption[] => {
      const currentId = selectedFunds[index]?.id;

      const selectedSet = new Set(selectedFundIds.filter((_, i) => i !== index));

      return fundOptions.filter((fund) => {
        if (fund.id === currentId) return true; // ✅ keep current selected
        return !selectedSet.has(fund.id!);
      });
    },
    [fundOptions, selectedFunds, selectedFundIds],
  );

  const hasValidPeriod = useMemo(() => {
    return ROLLING_PERIODS.some(([, value]) => value <= availableYears);
  }, [availableYears]);

  const handleCalculate = () => {
    const params = new URLSearchParams(searchParams.toString());

    // category id
    if (selectedCategory?.fund_cat_id) {
      params.set("catid", String(selectedCategory.fund_cat_id));
    }

    // funds → msoid = mso${id}, comma separated
    const fundIds = selectedFunds
      .map((f) => f?.id)
      .filter((id): id is number => Boolean(id))
      .map((id) => `mso${id}`);

    if (fundIds.length) {
      params.set("msoid", fundIds.join(","));
    }

    // start date
    if (selectedStartDate) {
      params.set("start", selectedStartDate);
    }

    // period
    if (selectedPeriod) {
      params.set("pd", selectedPeriod);
    }

    // benchmark → use ind_id
    if (selectedBenchmark?.ind_id) {
      params.set("bid", String(selectedBenchmark.ind_id));
    }

    startTransition(() => {
      router.push(`${pathname}?${params.toString()}`);
    });
  };

  useEffect(() => {
    if (isLoading) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  }, [isLoading]);

  return (
    <>
      <section className="section relative">
        <div className="p-5 sm:p-8 w-full shadow-[0px_12px_16px_-4px_#10182814] flex sm:flex-row flex-col gap-8 sm:gap-10 rounded-4xl sm:rounded-8xl bg-white mb-16">
          {/* left-side */}
          <div className="relative w-full">
            <div className="2xl:text-2xl 2xl:leading-8 font-semibold leading-1 mb-4 sm:mb-5">Select Mutual Funds (up to 4)</div>

            <div className="bg-white flex flex-col gap-5 sm:gap-6">
              {selectedFunds.map((selected, index) => (
                <RollingCalcDropdown
                  key={index}
                  index={index}
                  selected={selected}
                  options={getFilteredFunds(index)}
                  onSelect={(fund) => handleSelectFund(index, fund)}
                  onRemove={() => handleRemoveField(index)}
                />
              ))}
            </div>

            {selectedFunds.length < MAX_FIELDS && (
              <button
                onClick={handleAddField}
                aria-label="Add More Funds Button"
                className="flex items-center gap-2 px-6 py-2 justify-center mt-5 mx-auto rounded-full border border-primaryGreen text-primaryGreen font-semibold bg-white md:hover:bg-primaryGreen md:hover:text-white duration-300"
              >
                <Icon.addPlusIcon className="stroke-[1.5]" />
                Add A New Fund
              </button>
            )}
          </div>

          {/* right row */}
          <div className="w-full">
            <div className="text-base 2xl:text-2xl 2xl:leading-8 font-semibold leading-1 mb-4 sm:mb-5">Select a Category</div>

            <div className="bg-white flex flex-col gap-5 sm:gap-6 mb-4 sm:mb-5">
              {/* Category Dropdown */}
              <div className="relative">
                <button
                  ref={categoryButtonRef}
                  onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                  className="w-full p-2 sm:px-4 sm:py-5 rounded-lg sm:rounded-2xl border border-gray-300 bg-white text-left text-sm flex justify-between items-center"
                >
                  {selectedCategory?.fund_category ? (
                    selectedCategory.fund_category
                  ) : (
                    <span className="text-[#20202080]">Select a Category</span>
                  )}
                  <Icon.dropdownDownArrow
                    className={`shrink-0 fill-primaryBlack transition ${categoryDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Dropdown menu */}
                <CustomDropdown
                  isOpen={categoryDropdownOpen}
                  onClose={() => setCategoryDropdownOpen(false)}
                  excludeRefs={[categoryButtonRef]}
                  className="absolute p-1.5 top-full left-0 mt-2 w-full z-50 shadow-md rounded-lg sm:rounded-2xl overflow-hidden"
                >
                  <label className="block rounded-xl relative mb-1">
                    <Icon.searchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search category"
                      value={searchCategory}
                      onChange={(e) => setSearchCategory(e.target.value)}
                      className="w-full  pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg outline-hidden transition-all duration-200"
                    />
                  </label>

                  <ul className="divide-y divide-gray-100 max-h-60 overflow-y-auto scrollbar-xs">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((item) => (
                        <li key={item.fund_cat_id}>
                          <button
                            onClick={() => {
                              setSelectedCategory(item);
                              setCategoryDropdownOpen(false);
                              setSearchCategory("");
                            }}
                            className={`w-full text-left p-2 text-sm rounded-md ${item.fund_cat_id === selectedCategory?.fund_cat_id ? "bg-primaryGreen/10 text-primaryGreen font-semibold" : "hover:bg-primaryGreen/5"}`}
                          >
                            {item.fund_category}
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="px-4 py-2 ml-2 text-sm text-gray-400">No result found</li>
                    )}
                  </ul>
                </CustomDropdown>
              </div>

              {/* Start Date Dropdown */}
              <div className="relative">
                <button
                  ref={buttonRef}
                  onClick={() => setStartDateDropdownOpen(!startDateDropdownOpen)}
                  className="w-full p-2 sm:px-4 sm:py-5 rounded-lg sm:rounded-2xl border border-gray-300 bg-white text-left text-sm flex justify-between items-center"
                >
                  {selectedStartDate ? (
                    formatDate(selectedStartDate, "dd MMM yyyy")
                  ) : (
                    <span className="text-[#20202080]">Select a Start Date</span>
                  )}
                  <Icon.calendarIcon />
                </button>

                <CustomDropdown
                  isOpen={startDateDropdownOpen}
                  onClose={() => setStartDateDropdownOpen(false)}
                  excludeRefs={[buttonRef]}
                  className="absolute top-full left-0 mt-2 p-1.5 rounded-lg sm:rounded-2xl w-full z-50"
                >
                  <CustomCalendar
                    onDateSelect={(date) => {
                      setSelectedStartDate(formatDate(date, "yyyy-MM-dd"));
                      setStartDateDropdownOpen(false);
                    }}
                    minSelectableDate={minDateLimit}
                    maxSelectableDate={today}
                    fontSizeBase={false}
                    defaultSelectedDate={new Date(selectedStartDate)}
                  />
                </CustomDropdown>
              </div>
            </div>

            <div className="mb-4 sm:mb-5 font-semibold md:text-xl md:leading-7 2xl:text-2xl 2xl:leading-8 leading-1">Select Benchmark</div>

            <div className="relative mb-4 sm:mb-5">
              {/* Trigger button */}
              <button
                ref={benchmarkButtonRef}
                onClick={() => setBenchmarkDropdownOpen(!benchmarkDropdownOpen)}
                className="w-full p-2 sm:px-4 sm:py-5 rounded-lg sm:rounded-2xl border border-gray-300 bg-white text-left text-sm flex justify-between items-center"
              >
                {selectedBenchmark ? (
                  selectedBenchmark.stkexchg
                ) : (
                  <span className="text-[#20202080]">Select a Benchmark</span>
                )}

                <Icon.dropdownDownArrow className={`shrink-0 fill-primaryBlack transition  `} />
              </button>

              {/* Dropdown menu */}
              <CustomDropdown
                isOpen={benchmarkDropdownOpen}
                onClose={() => setBenchmarkDropdownOpen(false)}
                excludeRefs={[benchmarkButtonRef]}
                className="absolute top-full left-0 mt-2 w-full z-1 shadow-md rounded-lg sm:rounded-2xl overflow-hidden p-2"
              >
                <label className="block rounded-xl relative mb-1">
                  <Icon.searchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search Benchmark"
                    className="w-full  pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg outline-hidden transition-all duration-200"
                    value={searchBenchmark}
                    onChange={(e) => setSearchBenchmark(e.target.value)}
                  />
                </label>

                <ul className="divide-y divide-gray-100 max-h-60 overflow-y-auto scrollbar-xs">
                  {filteredBenchmark.length > 0 ? (
                    filteredBenchmark.map((item) => (
                      <li key={item.ind_id}>
                        <button
                          onClick={() => {
                            setSelectedBenchmark(item);
                            setBenchmarkDropdownOpen(false);
                            setSearchBenchmark("");
                          }}
                          className={`w-full text-left p-2 text-sm rounded-md ${item.stkexchg === selectedBenchmark?.stkexchg ? "bg-primaryGreen/10 text-primaryGreen font-semibold" : "hover:bg-primaryGreen/5"}`}
                        >
                          {item.stkexchg}
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-4 py-2 ml-2 text-sm text-gray-400">No result found</li>
                  )}
                </ul>
              </CustomDropdown>
            </div>

            <div className="mb-4 sm:mb-5 font-semibold md:text-xl md:leading-7 2xl:text-2xl 2xl:leading-8 leading-1">Select Rolling Period</div>

            <div className="w-full flex flex-wrap items-center gap-3 mb-4 sm:mb-5">
              {ROLLING_PERIODS.map(([label, value]) => {
                const isDisabled = value > availableYears;
                const isActive = Number(selectedPeriod) === value;

                return (
                  <button
                    key={label}
                    disabled={isDisabled}
                    onClick={() => setSelectedPeriod(String(value))}
                    className={`flex justify-center items-center py-2 px-10 rounded-full font-semibold border duration-300 ${isDisabled ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : isActive ? "bg-primaryGreen text-white border-primaryGreen" : "bg-white text-[#20202080] border-[#20202080] hover:border-primaryGreen hover:text-primaryGreen"}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleCalculate}
              disabled={!hasValidPeriod}
              className={`font-semibold w-full py-3 rounded-full border duration-300 text-white bg-primaryGreen border-primaryGreen ${!hasValidPeriod ? "opacity-60 cursor-not-allowed" : " hover:bg-white hover:text-primaryGreen"}`}
            >
              Calculate Rolling Returns
            </button>
          </div>
        </div>
      </section>

      {isLoading && <CustomPageLoader />}
    </>
  );
}
